import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import OnboardingLayout from "component/auth/OnboardingLayout";
import PaymentStepper from "component/pay/PaymentStepper";
import PinModal from "component/pay/PinModal";
import TransferSuccessModal from "component/pay/TransferSuccessModal";
import currencyPairCheck from "utilities/currencyPairCheck";
import { fetchTransferFees } from "api/fetchers";
import {
    selectUsdTransfer,
    selectUsdTransferPayload,
    resetUsdTransfer,
} from "redux/slices/usdTransferSlice";
import { resetBankTransfer } from "redux/slices/bankTransferSlice";
import { resetTransfer } from "redux/slices/tradevuTransferSlice";
import { resetCryptoTransfer } from "redux/slices/cryptoTransferSlice";
import { resetCryptoFiatTransfer } from "redux/slices/cryptoFiatTransferSlice";
import {
    selectCrossBorderTransfer,
    resetCrossBorderTransfer,
} from "redux/slices/crossBorderTransferSlice";
import ReviewDetailsCard from "./review-payment-details/ReviewDetailsCard";
import {
    buildFeeText,
    computeIsAlreadyBeneficiary,
    deriveCrossBorderUsdMeta,
    deriveRecipientDetails,
    deriveUsdReviewDetails,
    getYouSendText,
} from "./review-payment-details/helpers";
import { submitReviewTransfer } from "./review-payment-details/submitReviewTransfer";

const USD_KES_SAVE_CURRENCIES = ["USD", "KES"];

const getUsdAccountNumberFromPayload = (payload = {}) =>
    payload?.account?.account_number ||
    payload?.iban?.account_number ||
    payload?.swift?.account_number ||
    payload?.accountNumber ||
    "";

const buildSaveBeneficiaryStatusSnapshot = ({
    destinationCurrency,
    isBankRecipient,
    isCryptoToFiatFlow,
    isCrossBorder,
    bankRecipient,
    usdPayloadFromStore,
    cbUsd,
    cbFields,
    cfRecipient,
}) => {
    const currencyToSave = String(destinationCurrency || "").toUpperCase();
    if (!USD_KES_SAVE_CURRENCIES.includes(currencyToSave)) return null;

    let accountNumber = "";

    if (isBankRecipient) {
        accountNumber =
            currencyToSave === "USD"
                ? getUsdAccountNumberFromPayload(usdPayloadFromStore)
                : bankRecipient?.accountNumber || "";
    } else if (isCrossBorder) {
        accountNumber =
            currencyToSave === "USD"
                ? getUsdAccountNumberFromPayload(cbUsd)
                : cbFields?.accountNumber || "";
    } else if (isCryptoToFiatFlow) {
        accountNumber = cfRecipient?.accountIdentifier || "";
    }

    return accountNumber
        ? {
              accountNumber,
              currency: currencyToSave,
          }
        : null;
};

function ReviewPaymentDetails() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { business, currency } = useSelector((state) => state);

    const [isPinOpen, setIsPinOpen] = useState(false);
    const [beneficiaries, setBeneficiaries] = useState([]);
    const [showSuccess, setShowSuccess] = useState(false);
    const [feeLoading, setFeeLoading] = useState(false);
    const [backendFee, setBackendFee] = useState(0);
    const [transactionId, setTransactionId] = useState(null);
    const [beneficiaryRecipientSnapshot, setBeneficiaryRecipientSnapshot] = useState(null);
    const [saveBeneficiaryStatusSnapshot, setSaveBeneficiaryStatusSnapshot] = useState(null);
    const [transferCurrencySnapshot, setTransferCurrencySnapshot] = useState(null);
    const [destinationCurrencySnapshot, setDestinationCurrencySnapshot] = useState(null);

    const path = window.location.pathname;
    const isBankRecipient = path.includes("same-currency/bank-recipient");
    const isCryptoFlow = path.includes("crypto-wallet");
    const isCryptoToFiatFlow = path.includes("fiat-wallet");
    const isCrossBorder = path.includes("cross-border");
    const isTradevuTX = path.includes("tradevu-user");

    const {
        recipientAmount,
        recipientCurrency,
        bankRecipient,
        bankProvider,
        recipientMemo,
        recipientInvoice,
        recipientOtherDocument,
        crossBorderTransfer,
    } = useSelector((state) => state);

    const transferData = useSelector((state) => ({
        amount: state.amount,
        memo: state.memo,
        invoice: state.invoice,
        otherDocument: state.otherDocument,
        recipient: state.recipient,
        currency: state.currency,
        provider: state.provider,
    }));

    const cryptoTransfer = useSelector((state) => state.cryptoTransfer);
    const cryptoFiat = useSelector((state) => state.cryptoFiatTransfer);

    const cfAmount = String(cryptoFiat?.amount ?? "");
    const cfCoin = cryptoFiat?.currency || "USDC";
    const cfNetwork = cryptoFiat?.network || "";
    const cfRecipient = cryptoFiat?.recipient || {};
    const cfMemo = (cfRecipient?.memo || "").trim();
    const cfPayoutCurrency = (cfRecipient?.toCurrency || cfRecipient?.currency || "").toUpperCase();

    const usd = useSelector(selectUsdTransfer);
    const usdPayloadFromStore = useSelector(selectUsdTransferPayload);
    const isUsd = usd.currency === "USD";
    const isUsdKes = currency === "USD" || currency === "KES";

    const cb = useSelector(selectCrossBorderTransfer);
    const cbAmount = cb?.amount || "";
    const cbFrom = (cb?.fromCurrency || "").toUpperCase();
    const cbTo = (cb?.recipient?.toCurrency || "").toUpperCase();
    const cbFields = cb?.recipient?.fields || {};
    const cbBankName = cb?.recipient?.bankName || "";
    const cbMemo = (cb?.memo || "").trim();
    const cbInvoice = cb?.invoice || null;
    const cbOtherDoc = cb?.otherDocument || null;
    const {
        cbUsd,
        cbUsdType,
        cbUsdName,
        cbUsdBankName,
        cbUsdAccountNumber,
        cbUsdRouting,
        cbUsdBic,
        cbUsdCountry,
    } = useMemo(() => deriveCrossBorderUsdMeta(cb, cbBankName), [cb, cbBankName]);

    useEffect(() => {
        const fetchBeneficiaries = async () => {
            try {
                const currencyParam = isCrossBorder ? cbTo || currency : currency;
                const source = isCrossBorder
                    ? cb?.provider?.name || ""
                    : isBankRecipient
                      ? bankProvider?.name || ""
                      : "";
                const res = await axios.get("/v1/beneficiary/all", {
                    params: {
                        currency: currencyParam,
                        type: "bankTransfer",
                        ...(source ? { source } : {}),
                    },
                    headers: { "x-business-id": business._id },
                });
                setBeneficiaries(res.data?.data || []);
            } catch (err) {
                console.error("Failed to fetch beneficiaries", err);
            }
        };

        if (isBankRecipient || isCryptoToFiatFlow || isCrossBorder) fetchBeneficiaries();
    }, [
        business?._id,
        currency,
        isBankRecipient,
        isCryptoToFiatFlow,
        isCrossBorder,
        cbTo,
        bankProvider,
        cb?.provider,
    ]);

    const reviewAmountNum = useMemo(() => {
        if (isCryptoFlow) return Number(cryptoTransfer?.amount || 0);
        if (isCryptoToFiatFlow) return Number(cfAmount || 0);
        if (isCrossBorder) return Number(cbAmount || 0);
        if (isBankRecipient) return Number(recipientAmount || 0);
        return Number(transferData.amount || 0);
    }, [
        isCryptoFlow,
        isCryptoToFiatFlow,
        isCrossBorder,
        isBankRecipient,
        cryptoTransfer?.amount,
        cfAmount,
        cbAmount,
        recipientAmount,
        transferData.amount,
    ]);

    const { feeCurrencyFrom, feeCurrencyTo } = useMemo(() => {
        if (isCryptoFlow) {
            const token =
                (cryptoTransfer?.token || "").toUpperCase() || (currency || "").toUpperCase();
            return { feeCurrencyFrom: token, feeCurrencyTo: token };
        }

        if (isCryptoToFiatFlow) {
            return {
                feeCurrencyFrom: (cfCoin || "").toUpperCase(),
                feeCurrencyTo: cfPayoutCurrency,
            };
        }

        if (isCrossBorder) {
            return {
                feeCurrencyFrom: (cbFrom || currency || "").toUpperCase(),
                feeCurrencyTo: (cbTo || "").toUpperCase(),
            };
        }

        if (isBankRecipient) {
            const c = String(currency || "").toUpperCase();
            return { feeCurrencyFrom: c, feeCurrencyTo: c };
        }

        const c = String(transferData?.currency || currency || "").toUpperCase();
        return { feeCurrencyFrom: c, feeCurrencyTo: c };
    }, [
        isCryptoFlow,
        isCryptoToFiatFlow,
        isCrossBorder,
        isBankRecipient,
        cryptoTransfer?.token,
        currency,
        cfCoin,
        cfPayoutCurrency,
        cbFrom,
        cbTo,
        transferData?.currency,
    ]);

    const usdToUsdPaymentRail = useMemo(() => {
        if (feeCurrencyTo !== "USD") return "";

        const normalizeRail = (rail) => {
            const r = String(rail || "").toLowerCase();
            if (r === "iban") return "wire";
            return ["ach", "rtp", "wire", "swift"].includes(r) ? r : "";
        };

        const deriveRailFromAccountType = (accountType) => {
            const type = String(accountType || "").toLowerCase();
            if (type === "iban") return "wire";
            if (type === "international_wire" || type === "swift") return "swift";
            return "ach";
        };

        if (isCrossBorder) {
            return (
                normalizeRail(cbUsd?.paymentRail) ||
                deriveRailFromAccountType(cbUsd?.account_type || "us")
            );
        }

        return (
            normalizeRail(usdPayloadFromStore?.paymentRail || usd?.paymentRail) ||
            deriveRailFromAccountType(usdPayloadFromStore?.account_type || usd?.accountType || "us")
        );
    }, [
        feeCurrencyTo,
        isCrossBorder,
        cbUsd?.paymentRail,
        cbUsd?.account_type,
        usdPayloadFromStore?.paymentRail,
        usdPayloadFromStore?.account_type,
        usd?.paymentRail,
        usd?.accountType,
    ]);

    useEffect(() => {
        if (!business?._id || (isCrossBorder && !cb?.rateKey)) return;

        let cancelled = false;
        (async () => {
            try {
                setFeeLoading(true);
                const res = await fetchTransferFees({
                    businessId: business._id,
                    params: {
                        amount: Number.isFinite(reviewAmountNum) ? reviewAmountNum : 0,
                        currency: feeCurrencyFrom,
                        currencyTo: feeCurrencyTo,
                        ...(usdToUsdPaymentRail ? { paymentRail: usdToUsdPaymentRail } : {}),
                        ...(isCrossBorder ? { rateKey: cb.rateKey } : {}),
                    },
                });

                const raw = (res?.data ?? res) || {};
                const bag = raw.data || raw;
                const txnFee = Number(bag.transactionFee);
                if (!cancelled) {
                    setBackendFee(Number.isFinite(txnFee) ? txnFee : 0);
                }
            } finally {
                if (!cancelled) setFeeLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        business?._id,
        reviewAmountNum,
        feeCurrencyFrom,
        feeCurrencyTo,
        usdToUsdPaymentRail,
        isCrossBorder,
        cb?.rateKey,
    ]);

    const cbSymbol = useMemo(() => currencyPairCheck(cbFrom || currency), [cbFrom, currency]);

    const displayAmount = isBankRecipient
        ? recipientAmount
        : isCryptoFlow
          ? cryptoTransfer?.amount || 0
          : isCryptoToFiatFlow
            ? cfAmount || "0"
            : isCrossBorder
              ? String(cbAmount || "0")
              : transferData.amount;

    const displayMemo = isCryptoFlow
        ? cryptoTransfer?.naration
        : isCryptoToFiatFlow
          ? cfMemo
          : isBankRecipient
            ? recipientMemo
            : isCrossBorder
              ? cbMemo
              : transferData?.memo;

    const displayInvoiceName = isBankRecipient
        ? recipientInvoice?.name || "N/A"
        : isCryptoFlow
          ? cryptoTransfer?.invoice?.name || "N/A"
          : isCrossBorder
            ? cbInvoice?.name || "N/A"
            : transferData.invoice?.name || "N/A";

    const displayOtherDocName = isBankRecipient
        ? recipientOtherDocument?.name || "N/A"
        : isCryptoFlow
          ? cryptoTransfer?.supportDocument?.name || "N/A"
          : isCrossBorder
            ? cbOtherDoc?.name || "N/A"
            : transferData.otherDocument?.name || "N/A";

    const feeText = buildFeeText({
        feeLoading,
        isCryptoFlow,
        isCryptoToFiatFlow,
        isCrossBorder,
        isTradevuTX,
        backendFee,
        cryptoToken: cryptoTransfer?.token,
        cfCoin,
        cbFrom,
        currency,
        currencyPairCheck,
    });

    const recipientDetails = useMemo(
        () =>
            deriveRecipientDetails({
                isBankRecipient,
                isCryptoFlow,
                isCryptoToFiatFlow,
                isCrossBorder,
                bankRecipient,
                cryptoTransfer,
                cfRecipient,
                cfNetwork,
                cfPayoutCurrency,
                cbTo,
                cbFields,
                cbBankName,
                cbUsdName,
                cbUsdAccountNumber,
                cbUsdBankName,
                cbUsdType,
                cbUsdRouting,
                cbUsdBic,
                cbUsdCountry,
                transferData,
            }),
        [
            isBankRecipient,
            isCryptoFlow,
            isCryptoToFiatFlow,
            isCrossBorder,
            bankRecipient,
            cryptoTransfer,
            cfRecipient,
            cfNetwork,
            cfPayoutCurrency,
            cbTo,
            cbFields,
            cbBankName,
            cbUsdName,
            cbUsdAccountNumber,
            cbUsdBankName,
            cbUsdType,
            cbUsdRouting,
            cbUsdBic,
            cbUsdCountry,
            transferData,
        ]
    );

    const usdReviewDetails = useMemo(
        () => deriveUsdReviewDetails(usdPayloadFromStore, usd),
        [usdPayloadFromStore, usd]
    );

    const isAlreadyBeneficiary = computeIsAlreadyBeneficiary({
        isBankRecipient,
        isCryptoToFiatFlow,
        isCrossBorder,
        beneficiaries,
        cfRecipient,
        cbFields,
        bankRecipient,
    });

    const youSendText = getYouSendText({
        isCryptoFlow,
        isCryptoToFiatFlow,
        isCrossBorder,
        displayAmount,
        cryptoToken: cryptoTransfer?.token,
        cfCoin,
        cbSymbol,
        currency,
        currencyPairCheck,
    });

    const handleConfirmPin = async (pin) => {
        try {
            const reference = await submitReviewTransfer({
                pin,
                businessId: business?._id,
                isCryptoFlow,
                isCryptoToFiatFlow,
                isCrossBorder,
                isBankRecipient,
                currency,
                cryptoTransfer,
            cfAmount,
            cfCoin,
            cfNetwork,
            cfRecipient,
            cfMemo,
                cfPayoutCurrency,
                cb,
                cbTo,
                cbFrom,
                cbFields,
                cbBankName,
                cbMemo,
                cbInvoice,
                cbOtherDoc,
                cbUsd,
                cbAmount,
                cbSource: cb?.provider?.name || "",
                usd,
                usdPayloadFromStore,
                recipientMemo,
                recipientAmount,
                recipientInvoice,
                recipientOtherDocument,
                transferData,
                bankRecipient,
                bankProvider,
            });

            if (reference === undefined) return;

            const completedTransferCurrency = recipientCurrency || currency;
            const completedDestinationCurrency = isCrossBorder
                ? cbTo
                : recipientCurrency || currency;

            if (isBankRecipient && String(completedTransferCurrency || "").toUpperCase() === "NGN") {
                setBeneficiaryRecipientSnapshot({ ...bankRecipient });
            } else {
                setBeneficiaryRecipientSnapshot(null);
            }
            setSaveBeneficiaryStatusSnapshot(
                buildSaveBeneficiaryStatusSnapshot({
                    destinationCurrency: completedDestinationCurrency,
                    isBankRecipient,
                    isCryptoToFiatFlow,
                    isCrossBorder,
                    bankRecipient,
                    usdPayloadFromStore,
                    cbUsd,
                    cbFields,
                    cfRecipient,
                })
            );
            setTransferCurrencySnapshot(completedTransferCurrency);
            setDestinationCurrencySnapshot(completedDestinationCurrency);

            setTransactionId(reference);
            setIsPinOpen(false);
            setShowSuccess(true);

            dispatch(resetBankTransfer());
            dispatch(resetUsdTransfer());
            dispatch(resetTransfer());
            dispatch(resetCryptoTransfer());
            dispatch(resetCryptoFiatTransfer());
            dispatch(resetCrossBorderTransfer());
        } catch (error) {
            const apiMsg = error?.response?.data?.message || error?.message || "";
            if (/duplicate transaction/i.test(apiMsg)) {
                setIsPinOpen(false);
                toast.error("Temporary error. Please try again");
                return;
            }
            console.error("Transfer failed:", error?.response?.data || error.message);
            throw error;
        }
    };

    return (
        <OnboardingLayout title="Send money" showSkip={false}>
            <div className="max-w-3xl px-4 mx-auto mt-10 space-y-10">
                <PaymentStepper currentStep={3} />
                <h1 className="text-2xl font-normal text-[#101828]">Review payment details</h1>

                <ReviewDetailsCard
                    isCryptoFlow={isCryptoFlow}
                    isCryptoToFiatFlow={isCryptoToFiatFlow}
                    isCrossBorder={isCrossBorder}
                    isBankRecipient={isBankRecipient}
                    currency={currency}
                    youSendText={youSendText}
                    displayAmount={displayAmount}
                    feeText={feeText}
                    recipientDetails={recipientDetails}
                    crossBorderRate={crossBorderTransfer?.rate}
                    cbTo={cbTo}
                    usdReviewDetails={usdReviewDetails}
                    displayMemo={displayMemo}
                    displayInvoiceName={displayInvoiceName}
                    displayOtherDocName={displayOtherDocName}
                />

                <div className="flex gap-3 justify-end mt-6">
                    <button className="text-[#667085] font-medium" onClick={() => navigate(-1)}>
                        Back
                    </button>
                    <button
                        className="bg-[#6F00FF] hover:bg-[#5A00D6] text-white font-normal px-6 py-2 rounded-lg transition"
                        onClick={() => setIsPinOpen(true)}
                    >
                        Send {youSendText}
                    </button>
                </div>
            </div>

            <TransferSuccessModal
                isOpen={showSuccess}
                onClose={() => {
                    navigate("/wallets");
                }}
                onDone={() => navigate("/wallets")}
                transactionId={transactionId}
                isUsd={isUsd}
                isUsdKes={isUsdKes}
                isAlreadyBeneficiary={isAlreadyBeneficiary}
                beneficiaryRecipientSnapshot={beneficiaryRecipientSnapshot}
                saveBeneficiaryStatusSnapshot={saveBeneficiaryStatusSnapshot}
                transferCurrencySnapshot={transferCurrencySnapshot}
                destinationCurrencySnapshot={destinationCurrencySnapshot}
            />

            <PinModal
                isOpen={isPinOpen}
                onClose={() => setIsPinOpen(false)}
                onConfirm={handleConfirmPin}
            />
        </OnboardingLayout>
    );
}

export default ReviewPaymentDetails;
